import { getBuyNowRedirectLink, postDataForRepricingToLegacy } from '@/api/legacyAPI';
import { STEPS } from '@/core/checkoutSteps';
import {
    bikeCompanyComponents,
    bikeCompanySchemas,
    BikeFallback,
    BikeFallbackSchema,
    carCompanyComponents,
    carCompanySchemas,
    CarFallback,
    CarFallbackSchema,
    companyComponents,
    CompanyIDs,
    companySchemas,
    SharedFallback,
    SharedFallBackSchema,
} from '@/core/companyComponents';
import { licenseCategoryOptions, nationalityOptions, vehicleCatOptions } from '@/core/formOptions.js';
import { isEmptyValue, parseMoneyValue, trackGTMEvent, trackVarifyUniqueEvent } from '@/core/helpers';
import { AnalyticsService } from '@/core/services/analyticsService';
import { AutonomousProductsService } from '@/core/services/autonomousProductsService';
import { CheckoutFormDataService } from '@/core/services/checkoutFormDataService';
import { FormDataService } from '@/core/services/formDataService';
import { NEWSLETTER_OPTIN_IDENTIFIER, OptinsService } from '@/core/services/optinsService';
import { PackageService } from '@/core/services/packageService';
import { QuoteService } from '@/core/services/quoteService';
import { SelectedProductService } from '@/core/services/selectedProductService';
import { covers } from '@/core/textConstants';
import { InsuranceCovers, VehicleTypes } from "@/enums/filters";
import { PaymentMethods } from '@/enums/payments';
import { VarifyEvents } from '@/enums/varify';
import { useAPIStore } from '@/stores/api';
import { useStepperStore } from '@/stores/stepper';
import { OptinSchemaBuilder } from '@/views/checkouts/schemas/optinSchema';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { usePackagesStore } from './packages';

export const useCheckoutStore = defineStore('checkout', () => {
    const optins = ref({});
    const submitHandler = ref();
    const upsellCovers = ref([]);
    const displayedPrice = ref(0);
    const diledStatus = ref(null);
    const diledMessage = ref(null);
    const discountedPrice = ref(0);
    const selectedUpsells = ref([]);
    const discountApplied = ref(false);
    const discountedStatus = ref(null);
    const checkoutLoading = ref(false);
    const bankTransferPaymentActive = ref(false);
    const paymentMethod = ref(PaymentMethods.CARD);

    const apiStore = useAPIStore();
    const stepperStore = useStepperStore();
    const packagesStore = usePackagesStore();
    
    const quoteService = QuoteService();
    const optinsService = OptinsService();
    const packageService = PackageService();
    const formDataService = FormDataService();
    const selectedProductService = SelectedProductService();
    const checkoutFormDataService = CheckoutFormDataService();
    const autonomousProductsService = AutonomousProductsService();
    const analyticsService = AnalyticsService();


    const checkoutData = ref(checkoutFormDataService.get());
    const selectedProduct = ref(SelectedProductService().getSelectedProduct());

    const sanitizeCheckoutData = (data = {}) => {
        const sanitizedData = { ...data };

        if (!sanitizedData.usedVehicle) {
            delete sanitizedData.transferDate;
        }

        return sanitizedData;
    };

    const discountPercentage = computed(() => {
        if (!discountApplied.value) {
            return 0;
        }

        const originalPrice = displayedPrice.value;
        const currentPrice = discountedPrice.value;
        if (!originalPrice || !currentPrice || currentPrice >= originalPrice) {
            return 0;
        }

        const percent = ((originalPrice - currentPrice) / originalPrice) * 100;
        return Math.max(0, Math.round(percent));
    });

    const activeStepId = computed(() => {
        const stepIndex = Math.max((stepperStore.currentStep ?? 1) - 1, 0);
        return stepperStore.steps?.[stepIndex]?.id ?? null;
    });

    const isUploadStep = computed(() => {
        return activeStepId.value === STEPS.UPLOAD.id;
    });

    const isBankTransferPaymentStep = computed(() => {
        return activeStepId.value === STEPS.PAYMENT.id
            && paymentMethod.value === PaymentMethods.BANK_TRANSFER
            && bankTransferPaymentActive.value
    });

    const getCheckoutData = () => {
        const quote = quoteService.get();
        const updatedCheckoutData = sanitizeCheckoutData({ ...checkoutData.value });
        const formData = formDataService.get(quote.vehicleType);
        const productStartDate = selectedProductService.getStartDate();
        const filterStartDate = new Date(quote.startingDate);
        const startDate = filterStartDate > productStartDate ? filterStartDate : productStartDate;
        updatedCheckoutData.startDate = {
            day: startDate.getDate(),
            month: startDate.getMonth() + 1,
            year: startDate.getFullYear(),
        };

        const isIntersalonika = selectedProductService.getSelectedProductVendor().guid === CompanyIDs.INTERSALONIKA;
        const isApeironGroupama = selectedProductService.getSelectedProductVendor().guid === CompanyIDs.APEIRON
            || selectedProductService.getSelectedProductVendor().guid === CompanyIDs.GROUPAMA;

        // Initiate default values
        updatedCheckoutData.co_owner = false
        updatedCheckoutData.copy_address = true
        updatedCheckoutData.epitideumatias = false
        updatedCheckoutData.occupation_type = false
        updatedCheckoutData.vehicle = formData.vehicle
        updatedCheckoutData.email = formData.email ?? ''
        updatedCheckoutData.postcode = formData.postalCode
        updatedCheckoutData.birthDate = formData.birthDate
        updatedCheckoutData.birthDate2 = formData.birthDate2
        updatedCheckoutData.job_discount = formData.job !== 0
        updatedCheckoutData.only_driver = !formData.secondDriver
        updatedCheckoutData.license_plate = formData.licensePlate
        updatedCheckoutData.license_years = formData.licenseYears
        updatedCheckoutData.license_years2 = formData.licenseYears2

        if (
            updatedCheckoutData.license_years2 === 0
            && !updatedCheckoutData.only_driver
            && !updatedCheckoutData.licenseIssueDate2?.day
        ) {
            updatedCheckoutData.licenseIssueDate2 = {
                year: String(new Date().getFullYear()),
                month: '01',
                day: '01',
            };
        }

        updatedCheckoutData.legal_entity = formData.customerType === 3
        updatedCheckoutData.usedVehicle = formData.usedVehicle ?? false
        updatedCheckoutData.nationality = updatedCheckoutData.nationality ?? nationalityOptions[0].value
        updatedCheckoutData.nationality2 = updatedCheckoutData.nationality2 ?? nationalityOptions[0].value
        updatedCheckoutData.license_cat = updatedCheckoutData.license_cat ?? (isIntersalonika ? vehicleCatOptions[0].value : licenseCategoryOptions[0].value)
        updatedCheckoutData.license_cat2 = updatedCheckoutData.license_cat2 ?? (isApeironGroupama ? vehicleCatOptions[0].value : licenseCategoryOptions[0].value)

        if (updatedCheckoutData.usedVehicle) {
            updatedCheckoutData.transferDate = formData.transferDate
        } else {
            delete updatedCheckoutData.transferDate
        }

        return updatedCheckoutData
    }

    const syncDiledFieldsToFormData = (data) => {
        const quote = quoteService.get()
        const category = quote?.vehicleType ?? VehicleTypes.Car;
        const storedFormData = formDataService.get(category) ?? {};

        const normalizeTransferDate = (transferDate) => ({
            month: transferDate?.month ?? '',
            year: transferDate?.year ?? '',
        });

        const incomingTransferDate = normalizeTransferDate(data?.transferDate);
        const currentTransferDate = normalizeTransferDate(storedFormData.transferDate);
        const nextUsedVehicle = data?.usedVehicle ?? Boolean(storedFormData.usedVehicle);
        const nextLicensePlate = data?.license_plate ?? storedFormData.licensePlate ?? '';
        const nextTransferDate = nextUsedVehicle ? incomingTransferDate : null;

        const nextData = sanitizeCheckoutData({
            ...storedFormData,
            licensePlate: nextLicensePlate,
            usedVehicle: nextUsedVehicle,
            transferDate: nextTransferDate,
        });

        const hasChanged = storedFormData.licensePlate !== nextData.licensePlate
            || Boolean(storedFormData.usedVehicle) !== nextData.usedVehicle
            || currentTransferDate.month !== (nextTransferDate?.month ?? '')
            || currentTransferDate.year !== (nextTransferDate?.year ?? '');

        if (hasChanged) {
            formDataService.set(category, nextData);
        }
    };

    const storeCheckoutData = (data) => {
        const sanitizedData = sanitizeCheckoutData(data);
        checkoutData.value = sanitizedData;
        syncDiledFieldsToFormData(sanitizedData);
        
        if(data.startDate) {
            const localStartDate = new Date(
                Number.parseInt(data.startDate.year, 10),
                Number.parseInt(data.startDate.month, 10) - 1,
                Number.parseInt(data.startDate.day, 10)
            );
            selectedProductService.setStartDate(localStartDate);
        }
        selectedProduct.value = getSelectedProduct()

        checkoutFormDataService.store(sanitizedData);
    }

    const resetCheckoutData = () => {
        checkoutData.value = {};
        checkoutFormDataService.clear();
    }

    const storeOptinsData = (optins) => {
        checkoutData.value.optins = optins
    }

    const getFormComponent = () => {
        const isEasyBuy = selectedProductService.getEasyBuyStatus();
        const isBike = getVehicleType() === VehicleTypes.Bike;
        const id = selectedProductService.getSelectedProductVendor().guid;

        if (isBike) {
            return bikeCompanyComponents[id] ?? BikeFallback;
        }

        if (isEasyBuy) {
            return carCompanyComponents[id] ?? CarFallback;
        }

        // Non-easybuy: shared component (base fields, no vehicle-specific additions)
        return companyComponents[id] ?? SharedFallback;
    }

    const getFormSchema = () => {
        const isEasyBuy = selectedProductService.getEasyBuyStatus();
        const isBike = getVehicleType() === VehicleTypes.Bike;
        const id = selectedProductService.getSelectedProductVendor().guid;

        if (isBike) {
            return bikeCompanySchemas[id] ?? BikeFallbackSchema;
        }

        if (isEasyBuy) {
            return carCompanySchemas[id] ?? CarFallbackSchema;
        }

        // Non-easybuy: shared schema (base fields, no color etc.)
        return companySchemas[id] ?? SharedFallBackSchema();
    }

    const getOptinsSchema = async () => {
        optins.value = await fetchOptins();
        return OptinSchemaBuilder(optins.value)
    }

    const getForm = (
        firstDriverAdditionalPremiumAge,
        secondDriverAdditionalPremiumAge,
        firstDriverAdditionalPremiumLicenseYears,
        secondDriverAdditionalPremiumLicenseYears,
        legalEntity,
        secondDriver,
        discountEntity,
        vehicleType
    ) => {
        const companySchema = getFormSchema();

        let schema = companySchema(
            firstDriverAdditionalPremiumAge,
            secondDriverAdditionalPremiumAge,
            firstDriverAdditionalPremiumLicenseYears,
            secondDriverAdditionalPremiumLicenseYears,
            legalEntity,
            secondDriver,
            discountEntity,
            vehicleType
        )

        return {
            component: getFormComponent(),
            schema: schema,
        }
    }

    const getSelectedProduct = () => {
        selectedProduct.value = selectedProductService.getSelectedProduct();
        return selectedProduct.value;
    }

    const setSubmitHandler = (handler) => {
        submitHandler.value = handler
    }

    const setPaymentMethod = (method) => {
        paymentMethod.value = method;
        
        if (method !== PaymentMethods.BANK_TRANSFER) {
            bankTransferPaymentActive.value = false;
        }
    }

    const setBankTransferPaymentActive = (isActive) => {
        bankTransferPaymentActive.value = isActive;
    }

    const setDiledStatus = (status) => {
        diledStatus.value = status;

        if (status === null) {
            diledMessage.value = null;
        }
    };

    const setDiledState = ({ status = null, message = null } = {}) => {
        diledStatus.value = status;
        diledMessage.value = message;
    };

    const resetDiledState = () => {
        setDiledState();
    };

    const hasFormEmail = () => {
        const quote = quoteService.get();
        const formData = formDataService.get(quote?.vehicleType) ?? {};

        return Boolean(formData?.email);
    };

    const isFormNewsletterConsented = () => {
        const quote = quoteService.get();
        const formData = formDataService.get(quote?.vehicleType) ?? {};

        return formData?.newsletter_optin === true;
    };

    const fetchOptins = async () => {
        const quote = quoteService.get();
        quote.insuranceCovers = [...quote.insuranceCovers, ...upsellCovers.value];
        
        const easyBuyStatus = selectedProductService.getEasyBuyStatus();
        const vendor = selectedProductService.getSelectedProductVendor();
        const primaryProduct = selectedProductService.getPrimaryProduct();
        const selectedProduct = selectedProductService.getSelectedProduct();
        const name = selectedProduct.payload.multiproduct.name;

        const optins = await optinsService.getOptins(
            quote,
            easyBuyStatus,
            vendor,
            primaryProduct,
            selectedProduct,
            name,
        );

        if (isFormNewsletterConsented()) {
            const newsletterOptin = optins?.find?.(
                (optin) => optin.identifier === NEWSLETTER_OPTIN_IDENTIFIER
            );
            if (newsletterOptin) {
                newsletterOptin.value = false;
            }
        }

        return optins
    }

    const FLOW_POLL_INTERVAL_MS = 3000;

    const isFlowRunning = (response) =>
        response?.errors?.flowRunning === '1' && response?.errors?.flowCompleted === '0';

    const isFlowCompleted = (response) =>
        response?.errors?.flowRunning === '0' && response?.errors?.flowCompleted === '1';

    const postCheckoutFormData = async (values, urlExt) => {
        checkoutLoading.value = true

        try {
            const { vehicle, ...payload } = sanitizeCheckoutData(values);
            const quote = quoteService.get();
            const fid = quote.fid;
            const category = quote.vehicleType;

            if (Array.from(selectedUpsells.value).length > 0) {
                await reQuoteUpsells();
            }

            const mappedOptins = optins.value.reduce((acc, optin) => {
                acc[optin.identifier] = optin.value;
                return acc;
            }, {});

            const dc = selectedProductService.getDc();
            const companySiteId = selectedProductService.getSelectedProductVendor().attributes.siteId;

            const response = await checkoutFormDataService.postForm(
                category,
                dc,
                fid,
                payload,
                mappedOptins,
                companySiteId,
                urlExt
            );

            if (isFlowCompleted(response)) {
                await stepperStore.nextStep();
                return null;
            }

            if (isFlowRunning(response)) {
                await new Promise((resolve) => {
                    const poll = async () => {
                        try {
                            const pollResponse = await checkoutFormDataService.postForm(
                                category,
                                dc,
                                fid,
                                payload,
                                mappedOptins,
                                companySiteId,
                                urlExt
                            );
                            if (isFlowCompleted(pollResponse)) {
                                resolve();
                                return;
                            }
                        } catch {}
                        setTimeout(poll, FLOW_POLL_INTERVAL_MS);
                    };
                    setTimeout(poll, FLOW_POLL_INTERVAL_MS);
                });

                await stepperStore.nextStep();
                return null;
            }

            const selectedProduct = getSelectedProduct()
            const offerCode = getOfferCode();
            const vehicleType = getVehicleType();
            const roadAssistanceSelected = getRoadAssistanceSelected();

            trackVarifyUniqueEvent(VarifyEvents.CheckoutCompleted);
            trackGTMEvent({
            	"event": "checkout_success",
            	"offer_code": offerCode,
            	"category": vehicleType,
            	"checkout_type": selectedProduct.easyBuy ? 'easy_buy' : 'non_easy_buy',
            	"complete_value": selectedProduct.price.toFixed(2),
            	"odikiExists": roadAssistanceSelected,
            	"upsell": Array.from(selectedUpsells.value).map((cover) => covers[cover] ?? cover).join(', ')
            })


            analyticsService.saveUpsellUserSelection(fid, { covers: Array.from(selectedUpsells.value) });
            return response
        } finally {
            checkoutLoading.value = false;
        }
    }

    const reQuoteUpsells = async () => {
        const { newPrice, newPackage } = await reQuote();
        displayedPrice.value = newPrice;
        
        const quote = quoteService.get();
        const autonomousProducts = autonomousProductsService.getPrices(quote);
        const proxyFeatureHandler = packagesStore.proxyFeatureHandler;
        
        let covers = [...upsellCovers.value, ...quote?.insuranceCovers]?.reduce((acc, key) => {
            acc = { ...acc, [key]: { attributes: {} } };
            return acc;
        }, {});

        const newPackageTransformed = packageService.transformPackage(
            newPackage, 
            quote.insuranceDuration,
            quote.startingDate,
            covers,
            autonomousProducts, 
            proxyFeatureHandler
        );
        selectedProduct.value = newPackageTransformed;
        
        quote.insuranceCovers = [...upsellCovers.value, ...quote?.insuranceCovers];

        await postDataForRepricingToLegacy(quote.fid, quote, quote.customerJourneyId);
        
        const payload = apiStore.createBuyNowPayload(
            quote.fid,
            newPackageTransformed,
            apiStore.generateSelectedMultiproduct(selectedProduct.value)
        );
        const initListingResponse = await getBuyNowRedirectLink(payload);

        selectedProductService.store(newPackageTransformed, initListingResponse?.offerCode, initListingResponse?.dc);
    }

    const updateUpsellSelection = async (product, isSelected) => {
        const quote = quoteService.get();

        const productPrice = product.price ?? 0;
        const selection = new Set(upsellCovers.value);
        const featureTypeGuid = product.featureTypeGuid;

        if (isSelected) {
            selection.add(featureTypeGuid);
            upsellCovers.value.push(featureTypeGuid);
            displayedPrice.value = displayedPrice.value + productPrice;
        } else {
            selection.delete(featureTypeGuid);
            const index = upsellCovers.value.indexOf(featureTypeGuid);
            if (index !== -1) {
                upsellCovers.value.splice(index, 1);
            }
            displayedPrice.value = displayedPrice.value - productPrice;
        }

        const selectedProductIncludedCovers = new Set(
            selectedProduct.value?.includedCoversFlat ?? quote?.insuranceCovers ?? []
        );
        const addedUpsells = upsellCovers.value.filter((guid) => !selectedProductIncludedCovers.has(guid));

        selectedUpsells.value = new Set([...quote.insuranceCovers, ...addedUpsells]);
        optins.value = await fetchOptins();
    }

    const getOfferCode = () => {
        return selectedProductService.getOfferCode();
    }

    const getVehicleType = () => {
        const quote = quoteService.get();
        return quote?.vehicleType ?? null;
    }

    const getRoadAssistanceSelected = () => {
        const quote = quoteService.get();
        return quote?.insuranceCovers.includes(InsuranceCovers.RoadsideAssistance) ?? false;
    }

    const retrieveMultiproductPrice = () => {
        const currentSelectedProduct = getSelectedProduct();
        if (!currentSelectedProduct) {
            return 0;
        }

        const baseNumberOfCovers = Array.isArray(currentSelectedProduct?.includedCoversFlat)
            ? currentSelectedProduct.includedCoversFlat.length
            : Number(currentSelectedProduct?.numberOfCovers) || 0;

        selectedProduct.value = {
            ...selectedProduct.value,
            numberOfCovers: baseNumberOfCovers,
        };
        selectedProductService.updateNumberOfCovers(baseNumberOfCovers);
        selectedUpsells.value = [];
        upsellCovers.value = [];
        displayedPrice.value = currentSelectedProduct?.price;

        const appliedDiscount = selectedProductService.getAppliedDiscount();
        if (appliedDiscount?.offerKey !== getSelectedOfferKey()) {
            resetDiscountState();
        }
    }

    // A coupon is bound to the offer (product + dc)
    const getSelectedOfferKey = () => {
        const productId = selectedProductService.getSelectedProduct()?.id ?? null;
        return `${productId}|${selectedProductService.getDc()}`;
    }

    const resetDiscountState = () => {
        discountedPrice.value = 0;
        discountApplied.value = false;
        discountedStatus.value = null;
    }

    const applyPopupPricing = (validationData) => {
        if (!validationData) {
            return;
        }

        const newPrice = validationData.newPrice ?? validationData.newPacketPrice;
        const quote = quoteService.get();

        if (!isEmptyValue(newPrice)) {
            const parsedPrice = parseMoneyValue(newPrice);
            if (parsedPrice !== null) {
                displayedPrice.value = parsedPrice;
                selectedProductService.updatePriceValue(parsedPrice);
            }
        }

        if (!isEmptyValue(validationData.newInsuredValue)) {
            formDataService.updateVehicleValue(quote.vehicleType, validationData.newInsuredValue)
        }

        if (!isEmptyValue(validationData.insuredValueMin)) {
            formDataService.updateVehicleMinRecommendedValue(quote.vehicleType, validationData.insuredValueMin)
        }

        if (!isEmptyValue(validationData.insuredValueMax)) {
            formDataService.updateVehicleMaxRecommendedValue(quote.vehicleType, validationData.insuredValueMax)
        }
    }

    const getSelectedProductDiscounts = () => {
        const validDiscountStep = STEPS.CHECKOUT.value === stepperStore.currentStep;
        if (!validDiscountStep) {
            return false;
        }

        return selectedProductService.getDiscounts();
    }

    const applyDiscountCode = async (code) => {
        const quote = quoteService.get()
        const fid = quote.fid
        const dc = selectedProductService.getDc()
        const resp = await selectedProductService.applyDiscountFactory(code, fid, dc)
        const isDiscountValid = Boolean(resp?.success ?? resp?.isValid)

        if (isDiscountValid) {
            const { newPrice } = await reQuote(code)
            discountedPrice.value = newPrice;
            discountApplied.value = true;
            discountedStatus.value = 'success';
            selectedProductService.storeAppliedDiscount(
                getSelectedOfferKey(),
                selectedProductService.getSelectedProductVendor()?.guid,
                fid,
                dc
            );
        }

        return isDiscountValid
    }

    const reQuote = async (discountCode) => {
        const quote = quoteService.get()
        const formData = formDataService.get(quote.vehicleType)
        const multiproductId = selectedProductService.getSelectedProduct().payload.multiproduct._id
        const selectedCovers = [...upsellCovers.value, ...quote?.insuranceCovers]
        
        const updatedQuoteRequest = await quoteService.createQuoteRequest(formData, selectedCovers, null, checkoutData.value, multiproductId, discountCode);
        const { newPrice, newPackage } = await quoteService.reprice(updatedQuoteRequest);

        return {
            newPackage,
            newPrice
        }
    }

    return {
        getForm,
        getCheckoutData,
        setSubmitHandler,
        storeOptinsData,
        getOptinsSchema,
        submitHandler,
        checkoutData,
        optins,
        applyPopupPricing,
        checkoutLoading,
        storeCheckoutData,
        resetCheckoutData,
        getSelectedProduct,
        selectedProduct,
        postCheckoutFormData,
        displayedPrice,
        discountApplied,
        discountedPrice,
        discountPercentage,
        retrieveMultiproductPrice,
        paymentMethod,
        bankTransferPaymentActive,
        diledStatus,
        diledMessage,
        discountedStatus,
        isBankTransferPaymentStep,
        isUploadStep,
        setPaymentMethod,
        setBankTransferPaymentActive,
        setDiledStatus,
        setDiledState,
        resetDiledState,
        getSelectedProductDiscounts,
        applyDiscountCode,
        selectedUpsells,
        updateUpsellSelection,
        getOfferCode,
        getVehicleType,
        getRoadAssistanceSelected,
        hasFormEmail,
        isFormNewsletterConsented
    }
});
